import { useState } from 'react';
import { Send, CheckCircle, AlertCircle, MapPin, Phone, Mail } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactSection() {
  const [form, setForm] = useState({
    title: 'Monsieur',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    apartment: 'riesling',
    adults: 2,
    children_6_18: 0,
    children_0_5: 0,
    rental_type: 'week',
    date_from: '',
    date_to: '',
    linens: false,
    cleaning: false,
    message: '',
  });
  const [status, setStatus] = useState(null); // 'success' | 'error' | null
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    try {
      await axios.post(`${API}/contact`, form);
      setStatus('success');
      setForm((prev) => ({ ...prev, first_name: '', last_name: '', email: '', phone: '', message: '', date_from: '', date_to: '' }));
    } catch (err) {
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full bg-transparent border-b border-cream-400 focus:border-wine-700 outline-none py-3 font-body text-sm text-earth-100 placeholder:text-earth-300 transition-colors duration-300";
  const labelClass = "font-body text-xs uppercase tracking-[0.15em] text-earth-300 mb-1 block";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-16 md:mb-20">
          <p className="font-accent text-wine-700 text-lg tracking-[0.2em] uppercase mb-4">
            Reservation
          </p>
          <h2 className="font-display text-4xl md:text-5xl text-earth-100 mb-6">
            Contactez-nous
          </h2>
          <p className="font-body text-earth-200 text-lg max-w-xl mx-auto leading-relaxed">
            Remplissez le formulaire ci-dessous pour reserver ou obtenir des informations complementaires
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-12 lg:gap-16">
          {/* Contact info */}
          <div className="lg:col-span-1">
            <div className="bg-earth-100 text-white p-8 md:p-10 h-full">
              <h3 className="font-display text-2xl mb-8">Nos coordonnees</h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <MapPin size={18} className="text-gold mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-body text-sm font-bold mb-1">M. Cyril GREWIS-RELIEF</p>
                    <p className="font-body text-sm text-white/70">
                      4, Rue du General De Gaulle<br />
                      Cour Renaissance<br />
                      F-68340 RIQUEWIHR<br />
                      FRANCE
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Phone size={18} className="text-gold flex-shrink-0" />
                  <a href="tel:+33632345143" className="font-body text-sm hover:text-gold transition-colors">
                    +33 6 32 34 51 43
                  </a>
                </div>
                <div className="flex items-center gap-4">
                  <Mail size={18} className="text-gold flex-shrink-0" />
                  <a href="mailto:info@gite-riquewihr.com" className="font-body text-sm hover:text-gold transition-colors">
                    info@gite-riquewihr.com
                  </a>
                </div>
              </div>

              <div className="mt-10 pt-8 border-t border-white/10">
                <h4 className="font-display text-lg mb-4">Modes de paiement</h4>
                <div className="flex flex-wrap gap-2">
                  {['Especes', 'Cheque', 'Cheques vacances', 'PayPal'].map((m) => (
                    <span key={m} className="px-3 py-1.5 border border-white/20 font-body text-xs text-white/70">
                      {m}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-white/10">
                <p className="font-body text-xs text-white/50">
                  Acompte : 30% du montant<br />
                  Caution : 300&euro; (non encaissee)
                </p>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-8">
              {/* Name row */}
              <div className="grid sm:grid-cols-3 gap-6">
                <div>
                  <label className={labelClass}>Civilite</label>
                  <select
                    name="title"
                    value={form.title}
                    onChange={handleChange}
                    data-testid="contact-title"
                    className={inputClass + " cursor-pointer bg-white"}
                  >
                    <option>Monsieur</option>
                    <option>Madame</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Prenom *</label>
                  <input
                    type="text"
                    name="first_name"
                    value={form.first_name}
                    onChange={handleChange}
                    required
                    data-testid="contact-first-name"
                    className={inputClass}
                    placeholder="Votre prenom"
                  />
                </div>
                <div>
                  <label className={labelClass}>Nom *</label>
                  <input
                    type="text"
                    name="last_name"
                    value={form.last_name}
                    onChange={handleChange}
                    required
                    data-testid="contact-last-name"
                    className={inputClass}
                    placeholder="Votre nom"
                  />
                </div>
              </div>

              {/* Email / Phone */}
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    data-testid="contact-email"
                    className={inputClass}
                    placeholder="votre@email.com"
                  />
                </div>
                <div>
                  <label className={labelClass}>Telephone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    data-testid="contact-phone"
                    className={inputClass}
                    placeholder="+33 ..."
                  />
                </div>
              </div>

              {/* Apartment / Rental type */}
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>Appartement</label>
                  <select
                    name="apartment"
                    value={form.apartment}
                    onChange={handleChange}
                    data-testid="contact-apartment"
                    className={inputClass + " cursor-pointer bg-white"}
                  >
                    <option value="riesling">Riesling (N&deg;4) - 65 m&sup2;</option>
                    <option value="muscat">Muscat (N&deg;2) - Duplex 75 m&sup2;</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Type de location</label>
                  <select
                    name="rental_type"
                    value={form.rental_type}
                    onChange={handleChange}
                    data-testid="contact-rental-type"
                    className={inputClass + " cursor-pointer bg-white"}
                  >
                    <option value="week">Semaine</option>
                    <option value="weekend">Week-end</option>
                    <option value="night">Nuitee</option>
                  </select>
                </div>
              </div>

              {/* Dates */}
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>Date d'arrivee *</label>
                  <input
                    type="date"
                    name="date_from"
                    value={form.date_from}
                    onChange={handleChange}
                    required
                    data-testid="contact-date-from"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Date de depart *</label>
                  <input
                    type="date"
                    name="date_to"
                    value={form.date_to}
                    onChange={handleChange}
                    required
                    data-testid="contact-date-to"
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Guests */}
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <label className={labelClass}>Adultes</label>
                  <input
                    type="number"
                    name="adults"
                    min="1"
                    max="8"
                    value={form.adults}
                    onChange={handleChange}
                    data-testid="contact-adults"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Enfants 6-18 ans</label>
                  <input
                    type="number"
                    name="children_6_18"
                    min="0"
                    max="6"
                    value={form.children_6_18}
                    onChange={handleChange}
                    data-testid="contact-children-6-18"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Enfants 0-5 ans</label>
                  <input
                    type="number"
                    name="children_0_5"
                    min="0"
                    max="4"
                    value={form.children_0_5}
                    onChange={handleChange}
                    data-testid="contact-children-0-5"
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Options */}
              <div className="flex flex-wrap gap-8">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    name="linens"
                    checked={form.linens}
                    onChange={handleChange}
                    data-testid="contact-linens"
                    className="w-5 h-5 border-2 border-cream-400 text-wine-700 focus:ring-wine-700 rounded-sm"
                  />
                  <span className="font-body text-sm text-earth-200 group-hover:text-earth-100 transition-colors">
                    Draps et linges (18&euro;/pers.)
                  </span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    name="cleaning"
                    checked={form.cleaning}
                    onChange={handleChange}
                    data-testid="contact-cleaning"
                    className="w-5 h-5 border-2 border-cream-400 text-wine-700 focus:ring-wine-700 rounded-sm"
                  />
                  <span className="font-body text-sm text-earth-200 group-hover:text-earth-100 transition-colors">
                    Forfait menage (40-50&euro;)
                  </span>
                </label>
              </div>

              {/* Message */}
              <div>
                <label className={labelClass}>Message</label>
                <textarea
                  name="message"
                  rows="4"
                  value={form.message}
                  onChange={handleChange}
                  data-testid="contact-message"
                  className={inputClass + " resize-none"}
                  placeholder="Vos questions ou demandes particulieres..."
                />
              </div>

              {/* Status messages */}
              {status === 'success' && (
                <div data-testid="contact-success-message" className="flex items-center gap-3 p-4 bg-sage-50 border border-sage-200 text-sage-700">
                  <CheckCircle size={18} />
                  <p className="font-body text-sm">Merci ! Votre demande a ete envoyee avec succes. Nous vous repondrons dans les plus brefs delais.</p>
                </div>
              )}
              {status === 'error' && (
                <div data-testid="contact-error-message" className="flex items-center gap-3 p-4 bg-wine-50 border border-wine-200 text-wine-700">
                  <AlertCircle size={18} />
                  <p className="font-body text-sm">Une erreur est survenue. Veuillez reessayer ou nous contacter directement par telephone.</p>
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-btn"
                className="inline-flex items-center gap-3 px-10 py-4 bg-wine-700 text-white font-body text-sm uppercase tracking-[0.2em] hover:bg-wine-800 transition-all duration-300 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send size={16} />
                {loading ? 'Envoi en cours...' : 'Envoyer ma demande'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
